import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import type React from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import createAppTheme from "theme";

interface ThemeContextType {
	mode: "light" | "dark";
	toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
	mode: "light",
	toggleTheme: () => {},
});

export const useThemeContext = () => useContext(ThemeContext);

export const ThemeContextProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const [mode, setMode] = useState<"light" | "dark">(() => {
		const prefersDark =
			window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false;
		return prefersDark ? "dark" : "light";
	});

	// follow system color-scheme changes dynamically
	useEffect(() => {
		const media = window.matchMedia?.("(prefers-color-scheme: dark)");
		if (!media) return;
		const listener = (e: MediaQueryListEvent) =>
			setMode(e.matches ? "dark" : "light");

		if (media.addEventListener) media.addEventListener("change", listener);
		else media.addListener?.(listener);

		return () => {
			if (media.removeEventListener)
				media.removeEventListener("change", listener);
			else media.removeListener?.(listener);
		};
	}, []);

	const muiTheme = useMemo(() => createAppTheme(mode), [mode]);

	const toggleTheme = () => {
		setMode((prev) => (prev === "light" ? "dark" : "light"));
	};

	return (
		<ThemeContext.Provider value={{ mode, toggleTheme }}>
			<MuiThemeProvider theme={muiTheme}>
				<CssBaseline />
				<GlobalStyles
					styles={{
						"*, *::before, *::after": { boxSizing: "border-box" },
						body: { margin: 0 },
					}}
				/>
				{children}
			</MuiThemeProvider>
		</ThemeContext.Provider>
	);
};
